import { useCallback } from 'react'
import { useResource } from '@/components/admin/shared/hooks/use-resource'
import type { RouterOutputs } from '@/lib/trpc'
import { trpc } from '../../../../lib/trpc.js'
import type {
  ProblemRelationship,
  ProblemRelationshipType,
  RelationshipStrength,
} from '../types'

interface CreateRelationshipParams {
  targetProblemId: string
  type: ProblemRelationshipType
  strength?: RelationshipStrength
  notes?: string | null
}

interface UpdateRelationshipParams {
  type?: ProblemRelationshipType
  strength?: RelationshipStrength
  notes?: string | null
}

/** Flattens the { outgoing, incoming } response into ProblemRelationship[]. */
function selectRelationships(
  rawData: RouterOutputs['admin']['questions']['getRelationships'] | undefined,
): ProblemRelationship[] {
  if (!rawData) return []

  const outgoing: ProblemRelationship[] = rawData.outgoing.map((r) => ({
    id: r.id,
    type: r.type as ProblemRelationshipType,
    strength: r.strength as RelationshipStrength,
    notes: r.notes,
    direction: 'outgoing' as const,
    sourceProblemId: r.sourceProblemId,
    targetProblemId: r.targetProblemId,
    relatedProblem: {
      id: r.targetProblem.id,
      title: r.targetProblem.title,
      externalId: r.targetProblem.externalId,
    },
    createdAt: r.createdAt as string,
    updatedAt: r.updatedAt as string,
  }))

  const incoming: ProblemRelationship[] = rawData.incoming.map((r) => ({
    id: r.id,
    type: r.type as ProblemRelationshipType,
    strength: r.strength as RelationshipStrength,
    notes: r.notes,
    direction: 'incoming' as const,
    sourceProblemId: r.sourceProblemId,
    targetProblemId: r.targetProblemId,
    relatedProblem: {
      id: r.sourceProblem.id,
      title: r.sourceProblem.title,
      externalId: r.sourceProblem.externalId,
    },
    createdAt: r.createdAt as string,
    updatedAt: r.updatedAt as string,
  }))

  return [...outgoing, ...incoming]
}

export function useRelationships(problemId: string | undefined) {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    if (!problemId) return
    return utils.admin.questions.getRelationships.invalidate({ problemId })
  }, [problemId, utils])

  const {
    items: relationships,
    loading,
    error,
    refresh: loadRelationships,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.questions.getRelationships.useQuery(
      { problemId: problemId! },
      { enabled: !!problemId },
    ),
    select: selectRelationships,
    invalidate,
    create: trpc.admin.relationships.create,
    update: trpc.admin.relationships.update,
    remove: trpc.admin.relationships.delete,
  })

  const swapMutation = trpc.admin.relationships.swap.useMutation({
    onSuccess: invalidate,
  })

  const createRelationship = useCallback(
    async (
      params: CreateRelationshipParams,
    ): Promise<ProblemRelationship | null> => {
      if (!problemId) return null

      const data = await create({
        sourceProblemId: problemId,
        targetProblemId: params.targetProblemId,
        type: params.type,
        strength: params.strength,
        notes: params.notes,
      })

      const newRelationship: ProblemRelationship = {
        id: data.relationship.id,
        type: data.relationship.type as ProblemRelationshipType,
        strength: data.relationship.strength as RelationshipStrength,
        notes: data.relationship.notes,
        direction: 'outgoing' as const,
        sourceProblemId: data.relationship.sourceProblemId,
        targetProblemId: data.relationship.targetProblemId,
        relatedProblem: data.relationship.relatedProblem,
        createdAt: data.relationship.createdAt as string,
        updatedAt: data.relationship.updatedAt as string,
      }

      return newRelationship
    },
    [problemId, create],
  )

  const updateRelationship = useCallback(
    async (
      relationshipId: string,
      params: UpdateRelationshipParams,
    ): Promise<ProblemRelationship | null> => {
      const data = await update({ id: relationshipId, ...params })

      return {
        id: data.relationship.id,
        type: data.relationship.type as ProblemRelationshipType,
        strength: data.relationship.strength as RelationshipStrength,
        notes: data.relationship.notes,
        direction: 'outgoing' as const,
        sourceProblemId: data.relationship.sourceProblem.id,
        targetProblemId: data.relationship.targetProblem.id,
        relatedProblem: {
          id: data.relationship.targetProblem.id,
          title: data.relationship.targetProblem.title,
          externalId: data.relationship.targetProblem.externalId,
        },
        createdAt: data.relationship.createdAt as string,
        updatedAt: data.relationship.updatedAt as string,
      }
    },
    [update],
  )

  const deleteRelationship = useCallback(
    async (relationshipId: string): Promise<boolean> => {
      await remove({ id: relationshipId })
      return true
    },
    [remove],
  )

  const swapRelationshipDirection = useCallback(
    async (relationshipId: string): Promise<boolean> => {
      await swapMutation.mutateAsync({ id: relationshipId })
      return true
    },
    [swapMutation],
  )

  return {
    relationships,
    loading,
    error,
    loadRelationships,
    createRelationship,
    updateRelationship,
    deleteRelationship,
    swapRelationshipDirection,
  }
}
